import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AppConfig } from '../../../Config';

import { Dropdown } from 'semantic-ui-react';
import { useToasts } from 'common/toast';
import LoadingSpinner from 'common/components/LoadingSpinner';

import { ControlContainer, PageContent, PageHeader, PageSection } from '../../../common/styles/page';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from '../../../common/components/Table';
import { StyledButton } from 'common/components/button/StyledButton';
import StatusLabel from 'common/components/StatusLabel';

import { currentUserCanManageAssessments } from '../../../common/selectors/auth';
import { debriefScheduleApi, useGetDebriefScheduleQuery } from '../api/debriefScheduleApi';
import { Participant } from "../../../store/generated/portal/assessments";
import { briefTokenApi } from '../api/briefTokenApi';
import {
  SCHEDULE_LIVE,
  SCHEDULE_NOT_STARTED,
  SURVEY_STATUS_UNKNOWN,
  SURVEY_HAS_NO_TOKEN,
  SURVEY_NOT_STARTED,
  SURVEY_IN_PROGRESS,
  SURVEY_COMPLETED,
  SURVEY_CANCELLED,
  SURVEY_EMAIL_NOT_DELIVERED,
  SURVEY_NO_RESPONSE,
  SURVEY_INCOMPLETE
} from '../constants';

import { CancelSurveyModal, EmailHistoryModal, InviteModal, ReminderModal } from './AssessmentSurveyDetailsModals';
import AssessmentSurveySummaryCard from './AssessmentSurveySummaryCard';
import DashboardCard from 'areas/dashboard/components/DashboardCard';

export const AssessmentSurveyDetails = () => {
  const rawId = useParams<{ id: string }>().id;
  const debriefScheduleId = Number(rawId);

  const canManageAssessments = useSelector(currentUserCanManageAssessments);
  const { addToast } = useToasts();

  const {
    data: details,
    error,
  } = useGetDebriefScheduleQuery(
    { id: debriefScheduleId || 0 },
    { skip: !debriefScheduleId || debriefScheduleId === 0 },
  );

  const [sendReminders] = debriefScheduleApi.useApiPortalV1AssessmentsDebriefSchedulesSendRemindersCreateMutation();
  const [sendRemindersToEmployees] = debriefScheduleApi.useApiPortalV1AssessmentsDebriefSchedulesSendRemindersToEmployeesCreateMutation();
  const [updateBriefToken] = briefTokenApi.usePortalBriefTokensPartialUpdateMutation();
  const [createBriefToken] = briefTokenApi.usePortalBriefTokensCreateMutation();

  const [reminderModal, setReminderModal] = useState<{
    open: boolean;
    participantId?: number | null;
    name?: string;
    email?: string;
    mode: 'single' | 'team';
  }>({
    open: false,
    participantId: null,
    name: '',
    email: '',
    mode: 'single',
  });

  const [inviteModal, setInviteModal] = useState<{
    open: boolean;
    participantId: number | null;
    name: string;
    email: string;
  }>({
    open: false,
    participantId: null,
    name: '',
    email: '',
  });

  const [historyModal, setHistoryModal] = useState<{
    open: boolean;
    name: string;
    email: string;
    items: {
      email: string;
      sent_date: string;
      status: string;
      error_message?: string | null;
    }[];
  }>({
    open: false,
    name: '',
    email: '',
    items: [],
  });

  const [cancelModal, setCancelModal] = useState<{
    open: boolean;
    participantId: number | null;
    briefTokenId: number | null;
    name: string;
    email: string;
    isCancelled: boolean;
  }>({
    open: false,
    participantId: null,
    briefTokenId: null,
    name: '',
    email: '',
    isCancelled: false,
  });

  const openRemindAllModal = () => {
    setReminderModal({
      open: true,
      mode: 'team',
      name: debriefSchedule?.name || '',
    });
  };

  const handleToggleSurveyStatus = async (briefTokenId: number, isCancelled: boolean) => {
    const newStatus = isCancelled ? SURVEY_NOT_STARTED : SURVEY_CANCELLED;
    const action = isCancelled ? 'reactivated' : 'cancelled';

    try {
      await updateBriefToken({
        id: briefTokenId,
        patchedPortalBriefToken: {
          survey_status: newStatus,
        },
      }).unwrap();

      addToast(`Survey ${action} successfully`, {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error: any) {
      addToast(`Error ${action.slice(0, -1)}ing survey: ${error?.data?.error || error?.message || 'Unknown error'}`, {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };


  const formatDate = (date: string | Date): string =>
    new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });

  if (!details) {
    return <LoadingSpinner />;
  }

  const debriefSchedule = details;
  const isDebriefScheduleLive = debriefSchedule?.status === SCHEDULE_LIVE;
  const isDebriefScheduleScheduled = debriefSchedule?.status === SCHEDULE_NOT_STARTED;

  // Sort participants alphabetically by name for display
  const sortedParticipants = [...(debriefSchedule.participants || [])].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' })
  );

  // Show error message if the data fetch failed
  if (error) {
    return (
      <PageContent>
        <PageHeader
          title="Error Loading Team Schedule"
          controls={
            <StyledButton
              label="Back to Assessments"
              width="150px"
              onClick={() => window.history.back()}
            />
          }
        />
      </PageContent>
    );
  }

  if (isNaN(debriefScheduleId)) {
    return (
      <PageContent>
        <PageHeader
          title="Schedule Not Found"
          controls={
            <StyledButton
              label="Back to Assessments"
              width="150px"
              onClick={() => window.history.back()}
            />
          }
        />
      </PageContent>
    );
  }

  const renderControls = () => (
    <ControlContainer justify="right" gap={true}>
      <Dropdown
        button
        icon="cog"
        floating
        className="icon"
        style={{ width: '38px', height: '35px', borderRadius: '8px' }}
        direction="left"
      >
        <Dropdown.Menu>
          <Dropdown.Item
            icon="send"
            text="Remind Team Participants"
            disabled={
              !canManageAssessments || !isDebriefScheduleLive
            }
            onClick={() => {
              openRemindAllModal();
            }}

          />
        </Dropdown.Menu>
      </Dropdown>
    </ControlContainer>
  );

  const getSurveyResponseStatusLabel = (status: number, lastEmailError?: string | null) => {
    let color = '#9e9e9e';
    let label = 'Unknown';

    switch (status) {
      case SURVEY_STATUS_UNKNOWN:
        label = 'Status Unknown';
        color = '#6E6E6E';
        break;
      case SURVEY_HAS_NO_TOKEN:
        label = 'No Invitation';
        color = '#7A7A7A';
        break;
      case SURVEY_NOT_STARTED:
        debriefSchedule.status === SCHEDULE_LIVE ? label = 'Not Started' : label = 'Scheduled';
        color = '#9A9A9A';
        break;
      case SURVEY_IN_PROGRESS:
        label = 'In Progress';
        color = '#1976d2';
        break;
      case SURVEY_COMPLETED:
        label = 'Completed';
        color = '#2e7d32';
        break;
      case SURVEY_CANCELLED:
        label = 'Cancelled';
        color = '#424242';
        break;
      case SURVEY_EMAIL_NOT_DELIVERED:
        label = 'Email Not Delivered';
        color = '#d32f2f';
        break;
      case SURVEY_NO_RESPONSE:
        label = 'No Response';
        color = '#374151';
        break;
      case SURVEY_INCOMPLETE:
        label = 'Incomplete';
        color = '#ef6c00';
        break;
    }

    const title =
      status === 6 && lastEmailError
        ? lastEmailError
        : undefined;

    return <StatusLabel color={color} title={title}>{label}</StatusLabel>;
  };


  function generateSurveyLink(participantToken: string): string {
    return `${AppConfig.surveyUrl}/ediV2/${participantToken}`;
  }

  const openReminderModal = (participant: {
    id: number;
    name: string;
    email: string;
  }) => {
    setReminderModal({
      open: true,
      participantId: participant.id,
      name: participant.name,
      email: participant.email,
      mode: 'single',
    });
  };

  const closeReminderModal = () => {
    setReminderModal(prev => ({ ...prev, open: false }));
  };

  const confirmReminder = async () => {
    if (reminderModal.mode === 'single' && reminderModal.participantId && debriefScheduleId > 0) {
      try {
        const result = await sendRemindersToEmployees({
          body: {
            employee_ids: [reminderModal.participantId],
            debrief_schedule_id: debriefScheduleId,
          },
        }).unwrap();

        if (result.success) {
          addToast(
            result.count > 0
              ? 'Reminder sent successfully.'
              : 'Reminder sent, but the participant may have already completed the survey.',
            { appearance: 'success', autoDismiss: true }
          );
        } else {
          addToast(`Failed to send reminder: ${result.error || 'Unknown error'}`, {
            appearance: 'error',
            autoDismiss: true,
          });
        }
      } catch (error: any) {
        addToast(`Error sending reminder: ${error?.data?.error || error?.message || 'Unknown error'}`, {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    } else if (reminderModal.mode === 'team' && debriefScheduleId > 0) {
      try {
        const result = await sendReminders({
          id: debriefScheduleId,
          debriefSchedule: {} as any,
        }).unwrap();

        if (result.success) {
          addToast(`Successfully sent ${result.count} reminder${result.count !== 1 ? 's' : ''}`, {
            appearance: 'success',
            autoDismiss: true,
          });
        } else {
          addToast(`Failed to send reminders: ${result.error || 'Unknown error'}`, {
            appearance: 'error',
            autoDismiss: true,
          });
        }
      } catch (error: any) {
        addToast(`Error sending reminders: ${error?.data?.error || error?.message || 'Unknown error'}`, {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    }

    closeReminderModal();
  };

  const openHistoryModal = (participant: any) => {
    const items = Array.isArray(participant.emailHistory)
      ? participant.emailHistory
      : [];

    setHistoryModal({
      open: true,
      name: participant.name,
      email: participant.email,
      items,
    });
  };

  const closeHistoryModal = () => {
    setHistoryModal(prev => ({ ...prev, open: false }));
  };

  const openCancelModal = (participant: Participant) => {
    setCancelModal({
      open: true,
      participantId: participant.id,
      briefTokenId: participant.briefTokenId ?? null,
      name: participant.name,
      email: participant.email,
      isCancelled: participant.status === SURVEY_CANCELLED,
    });
  };

  const closeCancelModal = () => {
    setCancelModal(prev => ({ ...prev, open: false }));
  };

  const confirmCancel = async () => {
    if (!cancelModal.briefTokenId) {
      addToast('Brief token ID not found', {
        appearance: 'error',
        autoDismiss: true,
      });
      closeCancelModal();
      return;
    }

    await handleToggleSurveyStatus(cancelModal.briefTokenId, cancelModal.isCancelled);
    closeCancelModal();
  };

  const openInviteModal = (participant: Participant) => {
    setInviteModal({
      open: true,
      participantId: participant.id,
      name: participant.name,
      email: participant.email,
    });
  };

  const closeInviteModal = () => {
    setInviteModal(prev => ({ ...prev, open: false }));
  };

  const confirmInvite = async () => {
    if (!inviteModal.participantId) {
      addToast('Participant ID not found', {
        appearance: 'error',
        autoDismiss: true,
      });
      closeInviteModal();
      return;
    }

    try {
      await createBriefToken({
        portalBriefToken: {
          employee_id: inviteModal.participantId,
          debrief_schedule_id: debriefScheduleId,
        },
      }).unwrap();

      addToast(`Invitation created for ${inviteModal.name}`, {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      console.error('Failed to create brief token:', error);
      addToast('Failed to create invitation', {
        appearance: 'error',
        autoDismiss: true,
      });
    }

    closeInviteModal();
  };

  return (
    <PageContent>
      <PageHeader
        title={`Team Schedule`}
        data-testid="team-schedule-header"
        controls={<StyledButton label="Back" width="80px" onClick={() => window.history.back()} />
        }
      />

      <DashboardCard
        title={debriefSchedule ? `${debriefSchedule.name} - ${debriefSchedule.title}` : ''}
        height="200px"
        controls={renderControls()}
      >
        <AssessmentSurveySummaryCard
          debriefSchedule={debriefSchedule}
          data={{
            total_requests: debriefSchedule.invitationCount,
            total_responses: debriefSchedule.responseCount,
          }}
        />
      </DashboardCard>

      <PageSection padtop>
        <Table>
          <TableHeader>
            <TableRow>
              <HeaderCell>Name</HeaderCell>
              <HeaderCell>Email</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell>Last Invite</HeaderCell>
              {canManageAssessments && (
                <HeaderCell></HeaderCell>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedParticipants.map(participant => (
              <TableRow key={participant.id}>
                <TableCell $firstcol>{participant.name}</TableCell>
                <TableCell>{participant.email}</TableCell>
                <TableCell>{getSurveyResponseStatusLabel(participant.status, participant.lastEmailError)}</TableCell>
                <TableCell>
                  {`${participant.lastEmailSentAt ? formatDate(participant.lastEmailSentAt) + (participant.reminderCount > 0 ? ' (reminder #' + participant.reminderCount + ')' : '') : 'N/A'}`}


                </TableCell>
                {canManageAssessments && (
                  <TableCell>
                    {participant.status === SURVEY_HAS_NO_TOKEN && (isDebriefScheduleLive || isDebriefScheduleScheduled) ? (
                      <StyledButton
                        label="Invite"
                        width="80px"
                        color='green'
                        onClick={() => openInviteModal(participant)}
                      />
                    ) : participant.status !== SURVEY_HAS_NO_TOKEN ? (
                      <Dropdown
                        button
                        icon="ellipsis vertical"
                        floating
                        className="icon"
                        style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                        direction="left"
                      >
                        <Dropdown.Menu>
                        {/* Send Reminder */}
                        <Dropdown.Item
                          icon="send"
                          text="Send Reminder"
                          disabled={
                            participant.status === SURVEY_COMPLETED ||
                            participant.status === SURVEY_HAS_NO_TOKEN ||
                            participant.status === SURVEY_CANCELLED ||
                            !isDebriefScheduleLive
                          }
                          onClick={e => {
                            e.stopPropagation();
                            openReminderModal(participant);
                          }}
                        />

                        {/* Open Member Survey */}
                        <Dropdown.Item
                          icon="file alternate outline"
                          text="Launch Member Survey"
                          disabled={
                            participant.status === SURVEY_COMPLETED ||
                            participant.status === SURVEY_HAS_NO_TOKEN ||
                            participant.status === SURVEY_CANCELLED ||
                            !isDebriefScheduleLive
                          }
                          onClick={e => {
                            e.stopPropagation();
                            const url = generateSurveyLink(participant.briefToken || '');
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                        />

                        {/* View Email History */}
                        <Dropdown.Item
                          icon="history"
                          text="View Email History"
                          disabled={
                            participant.status === SURVEY_HAS_NO_TOKEN ||
                            !participant.emailHistory ||
                            participant.emailHistory.length === 0
                          }
                          onClick={e => {
                            e.stopPropagation();
                            openHistoryModal(participant);
                          }}
                        />

                        {/* Cancel/Reactivate Member Survey */}
                        <Dropdown.Item
                          icon={participant.status === SURVEY_CANCELLED ? "redo" : "cancel"}
                          text={participant.status === SURVEY_CANCELLED ? "Reactivate Member Survey" : "Cancel Member Survey"}
                          disabled={
                            participant.status === SURVEY_COMPLETED ||
                            participant.status === SURVEY_HAS_NO_TOKEN ||
                            !isDebriefScheduleLive && !isDebriefScheduleScheduled
                          }
                          onClick={e => {
                            e.stopPropagation();
                            openCancelModal(participant);
                          }}
                        />

                        </Dropdown.Menu>
                      </Dropdown>
                    ) : null}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PageSection>

      <ReminderModal
        open={reminderModal.open}
        name={reminderModal?.name || ''}
        email={reminderModal.mode === 'single' ? (reminderModal.email ?? '') : ''}
        onClose={closeReminderModal}
        onConfirm={confirmReminder}
      />

      <EmailHistoryModal
        open={historyModal.open}
        name={historyModal.name}
        email={historyModal.email}
        items={historyModal.items}
        formatDate={formatDate}
        onClose={closeHistoryModal}
      />

      <CancelSurveyModal
        open={cancelModal.open}
        name={cancelModal.name}
        email={cancelModal.email}
        isCancelled={cancelModal.isCancelled}
        onClose={closeCancelModal}
        onConfirm={confirmCancel}
      />

      <InviteModal
        open={inviteModal.open}
        name={inviteModal.name}
        email={inviteModal.email}
        onClose={closeInviteModal}
        onConfirm={confirmInvite}
      />

    </PageContent>
  );
};

export default AssessmentSurveyDetails;
